import { PulseTrace } from '../../components/brand';
import { useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import Seo from '../../components/seo/Seo';
import { useTranslation } from 'react-i18next';

const Layout = styled.div`
  min-height: 100vh;
  color: var(--ink);
  background: var(--paper);
  padding: 160px 0 80px;
  position: relative;
  overflow: hidden;

  @media screen and (max-width: 768px) {
    padding: 130px 0 64px;
  }
`;

const Hero = styled.section`
  max-width: var(--page-width);
  margin: 0 auto;
  padding: 0 var(--gutter) 3.5rem;
`;

const Kicker = styled.p`
  text-transform: uppercase;
  letter-spacing: 0.32rem;
  font-size: 1.4rem;
  color: var(--muted);
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  font-size: clamp(4.4rem, 7.2vw, 10rem);
  line-height: 1.08;
  text-wrap: balance;
  font-weight: 600;
  margin: 0;
  color: var(--ink);
`;

const HeroSub = styled.p`
  margin-top: 1.8rem;
  max-width: 760px;
  font-size: 1.6rem;
  line-height: 1.7;
  color: var(--ink);

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const ContentGrid = styled.div`
  max-width: var(--page-width);
  margin: 0 auto;
  padding: 0 var(--gutter);
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 3rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.8rem;
`;

const Card = styled.div`
  border-radius: 4px;
  padding: 2rem;
  background: var(--surface);
  border: 1px solid var(--line);
  box-shadow: 0 8px 30px rgba(32, 33, 34, 0.04);
`;

const CardTitle = styled.h2`
  font-size: 1.6rem;
  margin: 0 0 1rem;
  color: var(--ink);
`;

const CardText = styled.p`
  font-size: 1.4rem;
  line-height: 1.7;
  color: var(--muted);
  margin: 0 0 1rem;
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 0.75rem;
  color: var(--muted);

  li {
    position: relative;
    padding-left: 1.2rem;
    font-size: 1.4rem;
    line-height: 1.6;
  }

  li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.55rem;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--muted);
  }
`;

const ContactRows = styled.div`
  display: grid;
  gap: 0.6rem;
  font-size: 1.4rem;

  a {
    color: var(--ink);
    text-decoration: none;
  }
`;

const HelperNote = styled.p`
  font-size: 1.4rem;
  color: var(--muted);
  margin: 0;

  a {
    text-decoration: none;
  }
`;

const FormCard = styled(Card)`
  padding: 2.4rem;
`;

const FormHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-bottom: 1.6rem;
`;

const FormTitle = styled.h2`
  font-size: 1.8rem;
  margin: 0;
`;

const FormSub = styled.p`
  font-size: 1.4rem;
  color: var(--muted);
  margin: 0;
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  color: var(--muted);
  font-size: 1.4rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.95rem 1rem;
  border-radius: 10px;
  border: 1px solid var(--line);
  background: var(--paper);
  color: var(--ink);
  font-size: 1.6rem;

  &:focus {
    border-color: var(--ink);
    box-shadow: 0 8px 30px rgba(32, 33, 34, 0.04);
    outline: none;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.95rem 1rem;
  border-radius: 10px;
  font-size: 1.6rem;
  line-height: 1.6;
  resize: vertical;
  min-height: 150px;
  border: 1px solid var(--line);
  background: var(--paper);
  color: var(--ink);

  &:focus {
    border-color: var(--ink);
    box-shadow: 0 8px 30px rgba(32, 33, 34, 0.04);
    outline: none;
  }
`;

const SubmitButton = styled.button`
  margin-top: 0.6rem;
  padding: 1rem 1.5rem;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: var(--ink);
  color: var(--paper);
  font-size: 1.4rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 30px rgba(32, 33, 34, 0.04);
  }

  min-height: 48px;
`;

const FormNotice = styled.p`
  font-size: 1.4rem;
  color: var(--muted);
  margin: 0;
`;

export const Contact = ({}) => {
  const { i18n } = useTranslation('common');
  const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const workLink = lang === 'fr' ? '/fr/nos-realisations' : '/en/our-work';
  const servicesLink = lang === 'fr' ? '/fr/services' : '/en/services';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [responseMessage, setResponseMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const copy =
    lang === 'fr'
      ? {
          titleStart: 'Vous avez',
          titleAccent: 'l’ambition',
          titleEnd: 'Donnons-lui forme',
          subheading:
            'Partagez un court résumé de vos objectifs, contraintes et échéancier. Nous examinons chaque demande et répondons avec une prochaine étape claire, sans pression inutile.',
          directLinesTitle: 'Lignes directes',
          directLinesNote: 'Vous pouvez aussi nous joindre par courriel.',
          includeTitle: 'Ce qui aide le plus',
          includeItems: [
            'Contexte de l’entreprise et industrie',
            'Objectif principal et impact visé',
            'Échéancier et contraintes clés',
            'Liens, documents ou maquettes utiles',
          ],
          nextTitle: 'Ce qui suit',
          nextItems: [
            'Nous analysons votre contexte et proposons une prochaine étape ciblée.',
            'Si c’est un bon fit, nous cadrons une première phase claire ensemble.',
            'Sinon, nous vous orienterons vers la meilleure option.',
          ],
          confidentiality:
            'Confidentialité par défaut. NDA possible dès le départ.',
          formTitle: 'Que construisons-nous ?',
          formSub: 'Un message court et précis suffit pour démarrer.',
          labels: {
            name: 'Nom',
            email: 'Courriel',
            message: 'Message',
          },
          button: 'Envoyer',
          seoTitle: 'Contact SOR SEO | Étude de besoins',
          seoDescription:
            'Partagez votre contexte, vos objectifs et votre échéancier. Nous répondons avec une prochaine étape claire, sans processus inutile.',
        }
      : {
          titleStart: 'Bring the',
          titleAccent: 'ambition',
          titleEnd: 'Let’s make it real',
          subheading:
            'Share a short summary of your goals, constraints, and timeline. We review each request and reply with a focused next step, without unnecessary pressure.',
          directLinesTitle: 'Direct lines',
          directLinesNote: 'You can also reach us directly via email.',
          includeTitle: 'Helpful to include',
          includeItems: [
            'Company context and industry',
            'Primary objective and desired impact',
            'Timeline and key constraints',
            'Links, materials, or references',
          ],
          nextTitle: 'What happens next',
          nextItems: [
            'We review the context and respond with a focused next step.',
            'If there’s a fit, we’ll scope a clear first phase together.',
            'If not, we’ll point you toward the right path.',
          ],
          confidentiality:
            'Confidential by default. NDA-friendly from day one.',
          formTitle: 'What are we building?',
          formSub: 'Short, precise messages are perfect to start.',
          labels: {
            name: 'Name',
            email: 'Email',
            message: 'Message',
          },
          button: 'Send message',
          seoTitle: 'Contact SOR SEO | Focused Intake',
          seoDescription:
            'Share your goals, constraints, and timeline. We respond with a clear next step without unnecessary process.',
        };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setResponseMessage('');
    const proxyURL =
      process.env.NODE_ENV === 'production'
        ? 'https://sor-seo.com'
        : 'http://localhost:5000';
    try {
      const response = await axios.post(`${proxyURL}/api/contact`, formData);
      if (response.data.message) {
        setResponseMessage(response.data.message);
        setSubmitted(true);
      }
    } catch (error) {
      setResponseMessage(
        lang === 'fr'
          ? 'Une erreur est survenue. Veuillez réessayer plus tard.'
          : 'An error occurred. Please try again later.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <Seo
        title={copy.seoTitle}
        description={copy.seoDescription}
        canonicalPath={`/${lang}/contact`}
      />
      <Hero>
        <Kicker>{lang === 'fr' ? 'Contact' : 'Contact'}</Kicker>
        <Title>
          {copy.titleStart} <span>{copy.titleAccent}</span>
          <br />
          {copy.titleEnd}
        </Title>
        <HeroSub>{copy.subheading}</HeroSub>
        <PulseTrace />
      </Hero>

      <ContentGrid>
        <Column>
          <Card>
            <CardTitle>{copy.directLinesTitle}</CardTitle>
            <CardText>{copy.directLinesNote}</CardText>
            <ContactRows>
              <span>
                Email:{' '}
                <a href="mailto:contact@sor-seo.com">contact@sor-seo.com</a>
              </span>

              {/* <span>{lang === 'fr' ? 'Toronto · HE' : 'Toronto · ET'}</span> */}
            </ContactRows>
          </Card>

          <Card>
            <CardTitle>{copy.includeTitle}</CardTitle>
            <InfoList>
              {copy.includeItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </InfoList>
          </Card>

          <Card>
            <CardTitle>{copy.nextTitle}</CardTitle>
            <InfoList>
              {copy.nextItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </InfoList>
          </Card>

          <HelperNote>
            {copy.confidentiality}{' '}
            {lang === 'fr' ? (
              <>
                Explorez{' '}
                <a href={servicesLink} style={{ color: 'var(--ink)' }}>
                  nos services
                </a>{' '}
                ou{' '}
                <a href={workLink} style={{ color: 'var(--ink)' }}>
                  notre portfolio
                </a>
                .
              </>
            ) : (
              <>
                Explore{' '}
                <a href={servicesLink} style={{ color: 'var(--ink)' }}>
                  services
                </a>{' '}
                or{' '}
                <a href={workLink} style={{ color: 'var(--ink)' }}>
                  recent work
                </a>
                .
              </>
            )}
          </HelperNote>
        </Column>

        <Column>
          <FormCard>
            <FormHeader>
              <FormTitle>{copy.formTitle}</FormTitle>
              <FormSub>{copy.formSub}</FormSub>
            </FormHeader>
            {submitted ? (
              <FormNotice role="status">{responseMessage}</FormNotice>
            ) : (
              <ContactForm onSubmit={handleSubmit}>
                {responseMessage && (
                  <FormNotice role="alert">{responseMessage}</FormNotice>
                )}
                <Field>
                  {copy.labels.name}
                  <Input
                    type="text"
                    autoComplete="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </Field>
                <Field>
                  {copy.labels.email}
                  <Input
                    type="email"
                    autoComplete="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </Field>
                <Field>
                  {copy.labels.message}
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </Field>
                <SubmitButton type="submit" disabled={submitting}>
                  {submitting
                    ? lang === 'fr'
                      ? 'Envoi…'
                      : 'Sending…'
                    : copy.button}
                </SubmitButton>
              </ContactForm>
            )}
          </FormCard>
        </Column>
      </ContentGrid>
    </Layout>
  );
};

export default Contact;
